import React, { createContext, useCallback, useEffect, useState } from "react";
import { Amplify, Auth, Hub } from "aws-amplify";
import awsConfig from "../../aws-exports";
import Loader from "../loader";
import Utils from "../../utils";
import { ADMIN_GROUP_NAME } from "../../helpers/constant";

// Ensure correct configuration for OAuth
awsConfig.oauth = {
    domain: process.env.REACT_APP_OAUTH_DOMAIN,
    scope: [
        "phone",
        "email",
        "openid",
        "profile",
        "aws.cognito.signin.user.admin",
    ],
    responseType: "code",
    redirectSignIn: process.env.NODE_ENV === "development" ? "http://localhost:3000/" : process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN,
    redirectSignOut: process.env.NODE_ENV === "development" ? "http://localhost:3000/" : process.env.REACT_APP_OAUTH_REDIRECT_SIGN_OUT,
};

// Initialize Amplify
Amplify.configure(awsConfig);

// AuthContext Definition
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [spinner, setSpinner] = useState(false);
    const [authState, setAuthState] = useState({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        userGroups: [],
        currentNonAdminGroup: null, // Add property to store the non-admin group
    });

    const checkUser = useCallback(async () => {
        try {
            setSpinner(true);
            const user = await Auth.currentAuthenticatedUser();
            const groups = user.signInUserSession.idToken.payload["cognito:groups"] || [];
            const nonAdminGroup = groups.find((group) => group !== ADMIN_GROUP_NAME) || null;

            Utils.setUser(user.username);
            Utils.setUserGroup(groups);

            setAuthState({
                user,
                isAuthenticated: true,
                isAdmin: groups.includes(ADMIN_GROUP_NAME),
                userGroups: groups,
                currentNonAdminGroup: nonAdminGroup, // Set non-admin group
            });
            setSpinner(false);
        } catch (error) {
            console.log(error)
            setAuthState({
                user: null,
                isAuthenticated: false,
                isAdmin: false,
                userGroups: [],
                currentNonAdminGroup: null, // Reset non-admin group
            });

            await Auth.federatedSignIn();
        } finally {
            setSpinner(false);
        }
    }, [setSpinner])

    const setUserGroups = (groups) => {
        const nonAdminGroup = groups.find((group) => group !== ADMIN_GROUP_NAME) || null;

        setAuthState((prevState) => ({
            ...prevState,
            userGroups: groups,
            isAdmin: groups.includes(ADMIN_GROUP_NAME),
            currentNonAdminGroup: nonAdminGroup, // Update non-admin group
        }));
    };

    // Function to retrieve the JWT token
    const getJwtToken = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            return user.signInUserSession.idToken.jwtToken;
        } catch (error) {
            console.error("Failed to retrieve JWT token:", error);
            return null;
        }
    };

    const refreshUser = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            const groups = user.signInUserSession.idToken.payload["cognito:groups"] || [];
            const nonAdminGroup = groups.find((group) => group !== ADMIN_GROUP_NAME) || null;
    
            setAuthState((prevState) => ({
                ...prevState,
                user: user,
                isAuthenticated: true,
                isAdmin: groups.includes(ADMIN_GROUP_NAME),
                userGroups: groups,
                currentNonAdminGroup: nonAdminGroup,
            }));
        } catch (error) {
            console.error("Failed to refresh user context:", error);
            setAuthState({
                user: null,
                isAuthenticated: false,
                isAdmin: false,
                userGroups: [],
                currentNonAdminGroup: null, // Reset non-admin group
            });
        }
    }

    useEffect(() => {
        const listener = Hub.listen("auth", ({ payload: { event, data, message } }) => {
            if (["signIn", "cognitoHostedUI"].includes(event)) {
                checkUser();
            } else if (event === "signOut") {
                setAuthState({
                    user: null,
                    isAuthenticated: false,
                    isAdmin: false,
                    userGroups: [],
                    currentNonAdminGroup: null, // Reset non-admin group
                });
            } else if (["signIn_failure", "cognitoHostedUI_failure"].includes(event)) {
                console.log(event, " = ", data);
                if (data.name === "InvalidIdentityPoolConfigurationException") {
                    console.log(" ------------------------InvalidIdentityPoolConfigurationException-------------------------------- ")
                    const onExceptionSignOut = async () => {
                        await Auth.signOut({ global: true });
                        localStorage.clear();
                        sessionStorage.clear();
                    }
                    onExceptionSignOut();
                }
            }
        });

        // initialize the authentication check for user
        checkUser();

        return () => Hub.remove("auth", listener);
    }, []);

    return (
        <AuthContext.Provider value={{ ...authState, setUserGroups, getJwtToken, refreshUser }}>
            {spinner && <Loader />}
            {children}
        </AuthContext.Provider>
    );
};
